<template>
  <div>
    <!-- Desktop Search Bar -->
    <div 
      class="hidden lg:flex px-3 py-2 gap-2 cursor-pointer hover:border-tertiary hover:bg-hover transition items-center rounded-xl lg:bg-surface lg:border text-tertiary" 
      @click="openModal"
    >
      <md-icon class="text-secondary"><IconSearch /></md-icon>
      <div class="pr-8">{{ $t('searchModal.searchForLocation') }}</div>
      <div class="flex items-center justify-center w-6 h-6 bg-hover text-tertiary rounded-md text-xs font-mono">
        /
      </div>
    </div>

    <!-- Mobile Search Button -->
    <md-icon-button class="lg:hidden" @click="openModal">
      <md-icon><IconSearch /></md-icon>
    </md-icon-button>

    <teleport to="body">
      <md-dialog 
        :open="isOpen" 
        @close="closeModal" 
        class="search-dialog"
        @cancel.prevent="closeModal"
        quick
      >
        <div slot="headline" class="search-header">
          <md-icon><IconSearch /></md-icon>
          <input
              type="text"
              ref="searchInput"
              v-model="searchTerm"
              @input="handleInput"
              :placeholder="$t('searchModal.placeholder')"
              class="search-input"
          />
          <md-icon-button @click="closeModal">
            <md-icon><IconClose /></md-icon>
          </md-icon-button>
        </div>
        
        <div slot="content" class="search-content">
          <!-- Suggestions List -->
          <md-list v-if="suggestions.length > 0">
            <md-list-item 
              v-for="suggestion in suggestions" 
              :key="suggestion.placePrediction.placeId"
              type="button" 
              @click="selectPlace(suggestion)"
            >
              <md-icon slot="start"><IconLocationOn /></md-icon>
              <div slot="headline">{{ suggestion.description }}</div>
            </md-list-item>
          </md-list>
          
          <div v-else>
            <div v-if="!placesAvailable && searchTerm.length > 0" class="text-secondary text-center p-4">
              {{ $t('searchModal.searchUnavailable') }}
            </div>

            <div v-else-if="searchTerm.length === 0 && recentlyViewed.length === 0" class="text-secondary text-center p-4">
              {{ $t('searchModal.noRecentlyViewed') }}
            </div>
            
            <div v-else-if="searchTerm.length === 0 && recentlyViewed.length > 0">
              <div class="recently-viewed-header">
                {{ $t('searchModal.recentlyViewed') }}
              </div>
              <md-list>
                <md-list-item 
                  v-for="search in recentlyViewed" 
                  :key="search.lat + '-' + search.lng" 
                  type="button"
                  @click="selectRecentlyViewed(search)"
                >
                  <md-icon slot="start"><IconHistory /></md-icon>
                  <div slot="headline">{{ search.name }}</div>
                </md-list-item>
              </md-list>
            </div>
            
            <div v-else-if="isLoading" class="flex justify-center p-8">
              <md-circular-progress indeterminate></md-circular-progress>
            </div>
            
            <div v-else class="text-secondary text-center p-4">
              {{ $t('searchModal.noResults') }}
            </div>
          </div>
        </div>
      </md-dialog>
    </teleport>
  </div>
</template>

<script setup>
import {ref, onMounted, nextTick, onUnmounted} from 'vue';
import * as Sentry from "@sentry/vue";
import {useRouter} from 'vue-router';
import {useI18n} from 'vue-i18n';

// Material Web Imports
import '@material/web/dialog/dialog.js';
import '@material/web/list/list.js';
import '@material/web/list/list-item.js';
import '@material/web/icon/icon.js';
import '@material/web/iconbutton/icon-button.js';
import '@material/web/progress/circular-progress.js';

import IconSearch from '~icons/material-symbols/search-rounded';
import IconClose from '~icons/material-symbols/close-rounded';
import IconLocationOn from '~icons/material-symbols/location-on-rounded';
import IconHistory from '~icons/material-symbols/history-rounded';


const isOpen = ref(false);
const searchTerm = ref('');
const suggestions = ref([]);
const isLoading = ref(false);
const placesAvailable = ref(false);
const recentlyViewed = ref([]);
const MAX_RECENT_SEARCHES = 5;
const searchInput = ref(null);

let AutocompleteSessionToken;
let AutocompleteSuggestion;
let Place;
const sessionToken = ref(null);
let searchRequestId = 0;

const router = useRouter();
const {t} = useI18n();

const loadRecentlyViewed = () => {
  try {
    const storedSearches = localStorage.getItem('recentlyViewed');
    recentlyViewed.value = storedSearches ? JSON.parse(storedSearches) : [];
  } catch (e) {
    Sentry.captureException(e);
    recentlyViewed.value = [];
  }
};

const saveRecentlyViewed = () => {
  try {
    localStorage.setItem('recentlyViewed', JSON.stringify(recentlyViewed.value));
  } catch (e) {
    Sentry.captureException(e);
  }
};

onMounted(() => {
  loadRecentlyViewed();

  const initializePlaces = async () => {
    try {
      const placesLib = await google.maps.importLibrary("places");
      AutocompleteSessionToken = placesLib.AutocompleteSessionToken;
      AutocompleteSuggestion = placesLib.AutocompleteSuggestion;
      Place = placesLib.Place;
      sessionToken.value = new AutocompleteSessionToken();
      placesAvailable.value = true;
    } catch (e) {
      Sentry.captureException(e);
    }
  };

  initializePlaces();

  // Add event listener for hotkey
  document.addEventListener('keydown', handleHotkey);
});

onUnmounted(() => {
  searchRequestId++;
  // Remove event listener when component is unmounted
  document.removeEventListener('keydown', handleHotkey);
});

const handleHotkey = (event) => {
  if (event.key === '/' && !isOpen.value && !event.target.matches('input, textarea')) {
    event.preventDefault(); // Prevent typing '/' in input fields
    openModal();
  }
};

const openModal = () => {
  isOpen.value = true;
  if (AutocompleteSessionToken) {
    sessionToken.value = new AutocompleteSessionToken();
  }
  nextTick(() => {
    if (searchInput.value) {
      searchInput.value.focus();
    }
  });
};

const closeModal = () => {
  searchRequestId++;
  isOpen.value = false;
  searchTerm.value = '';
  suggestions.value = [];
  sessionToken.value = null;
  isLoading.value = false;
};

const handleInput = async () => {
  const requestId = ++searchRequestId;
  suggestions.value = [];
  if (!AutocompleteSuggestion || !sessionToken.value || searchTerm.value.length <= 2) {
    isLoading.value = false;
    return;
  }

  isLoading.value = true;
  try {
    const request = {
      input: searchTerm.value,
      sessionToken: sessionToken.value,
    };
    const {suggestions: newSuggestions} = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
    if (requestId !== searchRequestId) return;
    suggestions.value = newSuggestions.map(s => ({
      description: s.placePrediction.text.toString(),
      placePrediction: s.placePrediction
    }));
  } catch (error) {
    if (requestId !== searchRequestId) return;
    Sentry.captureException(error);
    suggestions.value = [];
  } finally {
    if (requestId === searchRequestId) isLoading.value = false;
  }
};

const navigateToLocation = (location) => {
  router.push({
    name: 'LocationDetail',
    params: {
      lat: location.lat.toFixed(3),
      lng: location.lng.toFixed(3)
    },
    query: {
      name: location.name,
    }
  });
  closeModal();
};

const addRecentSearch = (search) => {
  const existingIndex = recentlyViewed.value.findIndex(s => s.lat === search.lat && s.lng === search.lng);
  if (existingIndex !== -1) {
    recentlyViewed.value.splice(existingIndex, 1); // Remove existing to move to top
  }
  recentlyViewed.value.unshift(search); // Add to the beginning
  if (recentlyViewed.value.length > MAX_RECENT_SEARCHES) {
    recentlyViewed.value.pop(); // Remove oldest if over limit
  }
  saveRecentlyViewed();
};

const selectPlace = async (suggestion) => {
  if (!Place || !sessionToken.value) {
    Sentry.captureMessage(t('searchModal.placesLibraryError'));
    closeModal();
    return;
  }

  try {
    const place = suggestion.placePrediction.toPlace();
    await place.fetchFields({
      fields: ['displayName', 'formattedAddress', 'location'],
      sessionToken: sessionToken.value
    });

    const newSearch = {
      name: place.displayName,
      lat: place.location.lat(),
      lng: place.location.lng()
    };

    addRecentSearch(newSearch);

    navigateToLocation(newSearch);

    sessionToken.value = new AutocompleteSessionToken();
    closeModal();
  } catch (error) {
    Sentry.captureException(error);
    closeModal();
  }
};

const selectRecentlyViewed = (search) => {
  navigateToLocation(search);
};
</script>

<style scoped>
@reference "../assets/main.css";

.search-dialog {
  max-width: 560px;
  width: 90vw;
  --md-dialog-container-color: var(--color-surface);
  --md-sys-color-surface-container-high: var(--color-surface);
  --md-sys-color-surface-container: var(--color-surface);
}

@media (max-width: 768px) {
  .search-dialog {
    margin-top: 16px;
    margin-bottom: auto;
  }
}

.search-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px !important;
  border-bottom: 1px solid var(--color-border-default);
  background: var(--color-surface);
}

.search-input {
  flex: 1;
  background: transparent;
  border: none;
  font-size: 1rem;
  color: var(--color-primary);
  padding: 8px 0;
}

.search-input:focus {
  outline: none;
}

.search-content {
  padding: 0;
  max-height: 60vh;
  overflow-y: auto;
  background: var(--color-surface);
}

md-list {
  --md-list-container-color: var(--color-surface);
  background: var(--color-surface);
}

.recently-viewed-header {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-secondary);
  padding: 8px 16px;
}



.search-modal-list-item {
  @apply p-2 cursor-pointer text-[var(--color-primary)] rounded-md hover:bg-hover;
}
</style>

